// Implements: F-DSG-001, F-UIX-007, F-UIX-020
/**
 * The profile's name in the editor, renamed in place (mockup v3 P5, the mockup's `.name-row`). Pure
 * presentation: the name with its "Renombrar el perfil" button, or, while renaming, the input with
 * "Guardar" and "Cancelar". Enter saves and Escape cancels; the hint says a rename creates no
 * version. A refusal is an alert under the input, which keeps what the person typed.
 *
 * The input takes the focus when it opens (through a ref: `autoFocus` is banned) and holds at most
 * 80 characters, the server's limit after cleaning (v3 says 60; the server is the authority).
 * While a rename is in flight nothing can be sent again: the input and both buttons are disabled.
 */

import { useEffect, useRef } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import inputStyles from "../../../shared/ui/atoms/Input.module.css";
import styles from "./ProfileEditor.module.css";

/** `DesignProfile.name`: 1..80 characters once cleaned. */
export const MAX_PROFILE_NAME_CHARS = 80;

export type ProfileNameFieldProps =
  | { readonly mode: "view"; readonly name: string; readonly onStart: () => void }
  | {
      readonly mode: "edit";
      readonly draft: string;
      readonly onDraftChange: (value: string) => void;
      readonly onSave: () => void;
      readonly onCancel: () => void;
      readonly pending: boolean;
      readonly error: string | null;
    };

export function ProfileNameField(props: ProfileNameFieldProps): ReactElement {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const editing = props.mode === "edit";
  useEffect(() => {
    if (editing) input.current?.focus();
  }, [editing]);

  if (props.mode === "view") {
    return (
      <div className={styles.field}>
        <span className={styles.label}>{t("design.editor.name")}</span>
        <div className={styles.nameRow}>
          <p className={styles.readOnlyValue}>{props.name}</p>
          <Button variant="ghost" size="sm" onClick={props.onStart}>
            {t("design.rename.action")}
          </Button>
        </div>
      </div>
    );
  }
  const errorId = "design-rename-error";
  const hintId = "design-rename-hint";
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor="design-rename">
        {t("design.editor.name")}
      </label>
      <div className={styles.nameRow}>
        <input
          ref={input}
          id="design-rename"
          className={inputStyles.input}
          value={props.draft}
          maxLength={MAX_PROFILE_NAME_CHARS}
          disabled={props.pending}
          aria-invalid={props.error !== null}
          aria-describedby={props.error === null ? hintId : `${hintId} ${errorId}`}
          onChange={(event) => {
            props.onDraftChange(event.target.value);
          }}
          onKeyDown={(event) => {
            // Enter would submit the profile form around the name: it renames instead.
            if (event.key === "Enter") {
              event.preventDefault();
              if (!props.pending) props.onSave();
            } else if (event.key === "Escape") {
              event.preventDefault();
              props.onCancel();
            }
          }}
        />
        <Button variant="primary" size="sm" loading={props.pending} disabled={props.pending} onClick={props.onSave}>
          {t("design.rename.save")}
        </Button>
        <Button variant="ghost" size="sm" disabled={props.pending} onClick={props.onCancel}>
          {t("design.rename.cancel")}
        </Button>
      </div>
      <span id={hintId} className={styles.hint}>
        {t("design.rename.hint")}
      </span>
      {props.error === null ? null : (
        <p id={errorId} className={styles.error} role="alert">
          {props.error}
        </p>
      )}
    </div>
  );
}
